import { useInfiniteQuery } from '@tanstack/react-query'
import { useRef } from 'react'
import { notificationsQueryKeys } from '../constants'
import api from '../../config/axios-config'
import { customQueryClient } from '../../config/query-client'

const normalizeEtag = (etag) =>
  typeof etag === 'string'
    ? etag.replace(/^W\//, '').replace(/^"(.*)"$/, '$1')
    : etag

/**
 * Fetches paginated notifications and supports conditional updates using `If-Match` + `ETag`.
 * @param {number} [size=15] - Page size used in `page=<number>&size=<number>`.
 * @param {Object} [options={}] - Additional options for `useInfiniteQuery`.
 * @returns {Object} - Infinite query result where each page contains `{ changed, etag, page, items }`.
 * Endpoint:
 * Service: GET /notifications-api/v1/notifications?page=<number>&size=<number>
 * Frontend proxy: GET /api/notifications?page=<number>&size=<number>
 * Important details:
 * `page` is 0-based and the server returns newest notifications first.
 * The `ETag` response header is an ISO-8601 UTC instant (for example `2025-11-19T14:03:12Z`).
 * Send the last seen `ETag` as `If-Match` on the next request; if nothing changed, the server can return `304`.
 * Recommended strategy:
 * Poll while the notifications panel is open (every 30-60s) and only update list data when the response is not `304`.
 */

export const useNotificationsGetAllQuery = (size = 15, options = {}) => {
  const latestEtagValueRef = useRef(null)
  const queryKey = [notificationsQueryKeys.NOTIFICATIONS_GET_ALL, size]

  const handleQuery = async ({ pageParam = 0 }) => {
    const cached = customQueryClient.getQueryData(queryKey)
    const hasCachedFirstPage = Boolean(cached?.pages?.[0])
    const shouldSendIfMatch =
      pageParam === 0 && latestEtagValueRef.current && hasCachedFirstPage

    const config = {
      method: 'get',
      params: {
        page: pageParam,
        size
      },
      validateStatus: (status) =>
        status === 304 || (status >= 200 && status < 300),
      headers: {
        ...(shouldSendIfMatch ? { 'If-Match': latestEtagValueRef.current } : {})
      }
    }

    const res = await api(`/api/notifications`, config)

    const etagRaw = res.headers?.etag || undefined
    const etagValue = normalizeEtag(etagRaw)

    if (res.status === 304) {
      return (
        cached?.pages?.[pageParam] ?? {
          changed: false,
          etag: etagValue ?? undefined,
          page: { last: true },
          items: []
        }
      )
    }

    if (pageParam === 0 && shouldSendIfMatch) {
      customQueryClient.invalidateQueries({
        queryKey: [notificationsQueryKeys.NOTIFICATIONS_UNREAD]
      })
    }

    if (pageParam === 0 && etagRaw) {
      latestEtagValueRef.current = etagValue
    }

    const data = res.data

    // Spring Data Page has content[] plus paging metadata such as totalElements, totalPages, number, size, etc.
    return {
      changed: true,
      etag: etagValue,
      page: data,
      items: data.content || []
    }
  }

  return useInfiniteQuery({
    queryKey,
    queryFn: handleQuery,
    initialPageParam: 0,
    getNextPageParam: (lastPage, _pages, lastPageParam) => {
      if (lastPage.page.last === true) {
        return undefined
      }
      return lastPageParam + 1
    },
    retry: false,
    ...options
  })
}
